import PropTypes from 'prop-types';
import { Controller, useFieldArray } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import { addYearsToApiDate } from '#/utils/date';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import TagInput from '#/modules/commonComponents/form/TagInput';
import {
  joinAccessories,
  parseAccessories,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/accessoriesTags';
import {
  createEmptyRepairRequestLine,
  formatRepairRequestLineProductLabel,
} from '#/modules/crmMgmt/aftersales/repairRequest/helper/formSerialize';

/** 僅 max-h 內捲；勿套 form-list--window（其含 min-height） */
const REPAIR_LINES_LIST_MAX_HEIGHT_STYLE = {
  maxHeight: 'min(40rem, calc(100vh - var(--form-list-chrome)))',
};

/**
 * 動態列卡片（UI_LAYOUT）：灰底標出卡片範圍；
 * --field-bg 只提供 label 上半的容器底色（遮蔽框線）；欄位本身維持面板底色，
 * 才不會與灰底卡片同色而像 disabled。
 */
const LINE_CARD_CLASS = cn(
  'rounded-md border border-gray-200 bg-gray-50 px-3 py-5',
  '[--field-bg:var(--color-gray-50)]',
);

const LINE_GRID_CLASS = cn(
  'grid grid-cols-1 gap-x-3 gap-y-3',
  '@lg/form-section:grid-cols-2',
);

const FULL_SPAN_CLASS = '@lg/form-section:col-span-2';

const LineRemoveButton = ({ index, onRemove, ariaLabel }) => (
  <Button
    type="button"
    variant="ghost"
    size="icon-sm"
    className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
    aria-label={ariaLabel}
    onClick={() => onRemove(index)}
  >
    <PiTrashBold className="size-4" />
  </Button>
);

LineRemoveButton.propTypes = {
  index: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

const RepairRequestLineRow = ({
  index,
  control,
  register,
  setValue,
  errors,
  line,
  onRemove,
  canRemove,
  fillWarrantyFromSalesDate,
}) => {
  const productLabel = formatRepairRequestLineProductLabel(line);
  const accessoriesId = `lines.${index}.accessories`;
  const accessoriesError = errors?.lines?.[index]?.accessories;

  function handleSalesDateChange(nextValue) {
    const salesDate = nextValue ?? '';
    setValue(`lines.${index}.salesDate`, salesDate, {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (!fillWarrantyFromSalesDate) {
      return;
    }
    const warrantyExpiryDate = addYearsToApiDate(salesDate, 1);
    if (!warrantyExpiryDate) {
      return;
    }
    setValue(`lines.${index}.warrantyExpiryDate`, warrantyExpiryDate, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }

  return (
    <li className={LINE_CARD_CLASS}>
      <div className="mb-4 flex min-w-0 items-center justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-medium text-gray-700">
          <span className="tabular-nums">{`項目# ${index + 1}`}</span>
          {productLabel ? (
            <span className="ml-2 font-normal text-gray-500">{productLabel}</span>
          ) : null}
        </p>
        {canRemove ? (
          <LineRemoveButton
            index={index}
            onRemove={onRemove}
            ariaLabel={`移除明細 ${index + 1}`}
          />
        ) : null}
      </div>

      <div className={LINE_GRID_CLASS}>
        <FloatingInput
          id={`lines.${index}.serialNo`}
          type="text"
          label="產品序號"
          register={register}
          errors={errors}
        />
        {line?.mtrSnId ? (
          <p className="text-sm text-gray-600">
            已綁定序號登錄
            {' '}
            {line.mtrSnId}
          </p>
        ) : null}
        <FloatingInput
          id={`lines.${index}.version`}
          type="text"
          label="版本"
          register={register}
          errors={errors}
        />
        <FloatingTextarea
          id={`lines.${index}.problemDescription`}
          label="問題描述"
          register={register}
          errors={errors}
          rows={2}
          containerClassName={FULL_SPAN_CLASS}
        />
        <div className={cn('min-w-0', FULL_SPAN_CLASS)}>
          <Controller
            name={accessoriesId}
            control={control}
            render={({ field }) => (
              <TagInput
                id={accessoriesId}
                label="配件（輸入後按 Enter 新增，可多個）"
                value={parseAccessories(field.value)}
                onChange={(tags) => field.onChange(joinAccessories(tags))}
                onBlur={field.onBlur}
                errorMessage={accessoriesError?.message}
              />
            )}
          />
        </div>
        {fillWarrantyFromSalesDate ? (
          <FloatingDateInput
            id={`lines.${index}.salesDate`}
            label="銷售日"
            value={line?.salesDate ?? ''}
            onChange={handleSalesDateChange}
            errors={errors}
            placeholder="西元年/月/日"
          />
        ) : (
          <FloatingDateInput
            id={`lines.${index}.salesDate`}
            label="銷售日"
            control={control}
            errors={errors}
            placeholder="西元年/月/日"
          />
        )}
        <FloatingDateInput
          id={`lines.${index}.warrantyExpiryDate`}
          label="保固迄日"
          control={control}
          errors={errors}
          placeholder="西元年/月/日"
        />
        <FloatingTextarea
          id={`lines.${index}.remark`}
          label="明細備註"
          register={register}
          errors={errors}
          rows={2}
          containerClassName={FULL_SPAN_CLASS}
        />
      </div>
    </li>
  );
};

RepairRequestLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  line: PropTypes.object,
  onRemove: PropTypes.func.isRequired,
  canRemove: PropTypes.bool,
  fillWarrantyFromSalesDate: PropTypes.bool,
};

const RepairRequestLinesEditor = ({
  control,
  register,
  setValue,
  errors,
  watch,
  fillWarrantyFromSalesDate = false,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lines',
  });
  const watchedLines = watch('lines') ?? [];
  const linesError = errors?.lines;
  const linesRootMessage = typeof linesError?.message === 'string'
    ? linesError.message
    : null;

  function handleAdd() {
    append(createEmptyRepairRequestLine());
  }

  return (
    <section className="@container/form-section">
      <div className="form-section__header">
        <h2 className="form-block__title mr-2">報修明細</h2>
        <Button type="button" size="sm" onClick={handleAdd}>
          新增明細
        </Button>
      </div>
      {linesRootMessage ? (
        <p className="form_error mt-2" role="alert">{linesRootMessage}</p>
      ) : null}

      <div
        className="mt-3 min-h-0 overflow-y-auto p-1"
        style={REPAIR_LINES_LIST_MAX_HEIGHT_STYLE}
      >
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {fields.length === 0 ? (
            <li className="form-section__empty-state px-3 py-8 text-center text-sm text-gray-500">
              尚無明細，請新增至少一筆
            </li>
          ) : (
            fields.map((field, index) => (
              <RepairRequestLineRow
                key={field.id}
                index={index}
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                line={watchedLines[index]}
                onRemove={remove}
                canRemove={fields.length > 1}
                fillWarrantyFromSalesDate={fillWarrantyFromSalesDate}
              />
            ))
          )}
        </ul>
      </div>
    </section>
  );
};

RepairRequestLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func,
  errors: PropTypes.object,
  watch: PropTypes.func.isRequired,
  fillWarrantyFromSalesDate: PropTypes.bool,
};

export default RepairRequestLinesEditor;
